import { Controller } from "@hotwired/stimulus"

// Simple user autocomplete for admin forms.
// Usage (user_id mode, default):
//   <div data-controller="user-select">
//     <input type="text"
//            data-user-select-target="input"
//            data-action="input->user-select#search blur->user-select#maybeHideResults focus->user-select#maybeShowResults">
//     <input type="hidden" name="user_pricing[user_id]"
//            data-user-select-target="hidden">
//     <div data-user-select-target="results" class="user-select-results"></div>
//   </div>
//
// Usage (email mode, e.g. Add member by email):
//   <div data-controller="user-select"
//        data-user-select-url-value="/admin/organisations/123/users_lookup"
//        data-user-select-value-key-value="email">
//     <input type="text" name="email"
//            data-user-select-target="input"
//            data-action="input->user-select#search blur->user-select#maybeHideResults focus->user-select#maybeShowResults">
//     <div data-user-select-target="results" class="user-select-results"></div>
//   </div>
//
// Expects lookup endpoint to return:
//   [{ id, email, name, company }, ...]
export default class extends Controller {
  static targets = ["input", "hidden", "results", "firstName", "lastName"]
  static values = {
    url: String,
    valueKey: { type: String, default: "id" }
  }

  connect () {
    this.abortController = null
    this.hideTimeout = null
  }

  disconnect () {
    this._cancelRequest()
    this._clearHideTimeout()
  }

  search () {
    const term = this.inputTarget.value.trim()

    // Clear selection if input was cleared
    if (term === "") {
      if (this.hasHiddenTarget) this.hiddenTarget.value = ""
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.style.display = "none"
      return
    }

    this._debouncedFetch(term)
  }

  select (event) {
    const id = event.currentTarget.getAttribute("data-id")
    const label = event.currentTarget.getAttribute("data-label")
    const email = event.currentTarget.getAttribute("data-email")
    const firstName = event.currentTarget.getAttribute("data-first-name")
    const lastName = event.currentTarget.getAttribute("data-last-name")

    if (!id) return

    if (this.valueKeyValue === "email" && email) {
      this.inputTarget.value = email
      if (this.hasFirstNameTarget) this.firstNameTarget.value = firstName || ""
      if (this.hasLastNameTarget) this.lastNameTarget.value = lastName || ""
    } else {
      if (this.hasHiddenTarget) this.hiddenTarget.value = id
      this.inputTarget.value = label
    }
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.style.display = "none"
  }

  maybeHideResults () {
    // Delay hiding so clicks on results still register
    this._clearHideTimeout()
    this.hideTimeout = setTimeout(() => {
      this.resultsTarget.style.display = "none"
    }, 150)
  }

  maybeShowResults () {
    if (this.resultsTarget.innerHTML.trim() !== "") {
      this.resultsTarget.style.display = "block"
    }
  }

  // Internal

  _debouncedFetch (term) {
    if (this.fetchTimeout) {
      clearTimeout(this.fetchTimeout)
    }

    this.fetchTimeout = setTimeout(() => {
      this._performFetch(term)
    }, 200)
  }

  async _performFetch (term) {
    this._cancelRequest()

    if (!term || term.length < 2) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.style.display = "none"
      return
    }

    this.abortController = new AbortController()

    const url = this.hasUrlValue
      ? `${this.urlValue}?q=${encodeURIComponent(term)}`
      : `/admin/users/lookup?q=${encodeURIComponent(term)}`

    try {
      const response = await fetch(url, {
        headers: { "Accept": "application/json" },
        signal: this.abortController.signal
      })

      if (!response.ok) return

      const data = await response.json()
      this._renderResults(data)
    } catch (e) {
      if (e.name === "AbortError") return
      // Fail silently; this is an admin nicety, not critical path
      console.warn("User lookup failed", e)
    }
  }

  _renderResults (users) {
    if (!Array.isArray(users) || users.length === 0) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.style.display = "none"
      return
    }

    const items = users.map((u) => {
      const labelParts = [u.name || u.email]
      if (u.company) labelParts.push(`(${u.company})`)
      const label = labelParts.join(" ")
      const escapedLabel = this._escapeHtml(label)
      const escapedEmail = this._escapeHtml(u.email || "")
      const escapedFirstName = this._escapeHtml(u.first_name || "")
      const escapedLastName = this._escapeHtml(u.last_name || "")
      const sameDomainBadge = u.same_domain
        ? '<span class="badge bg-light text-dark border ms-1">same domain</span>'
        : ""

      return `
        <button type="button"
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center text-nowrap"
                data-id="${u.id}"
                data-label="${escapedLabel}"
                data-email="${escapedEmail}"
                data-first-name="${escapedFirstName}"
                data-last-name="${escapedLastName}"
                data-action="click->user-select#select">
          <span>
            ${escapedLabel}
            ${sameDomainBadge}
          </span>
          <small class="text-muted ms-2">${escapedEmail}</small>
        </button>
      `
    }).join("")

    this.resultsTarget.innerHTML = `<div class="list-group user-select-results-dropdown">${items}</div>`
    this.resultsTarget.style.display = "block"
  }

  _cancelRequest () {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  _clearHideTimeout () {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
  }

  _escapeHtml (str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }
}

